import { useCallback, useMemo } from 'react';
import { useSearchParamsState } from 'src/hooks/useSearchParamsState';
import {
  currentWeekFilter,
  tempOptions,
} from 'src/routes/Jobs/Timesheets/components/WeekSelector';
import { IDateOption } from 'src/routes/Jobs/Timesheets/types';
import {
  readTimeViewParams,
  writeTimeViewParams,
} from 'src/routes/Time/timeSearchParams';

export interface IUseTimeViewParamsResult {
  tab: string | null;
  /** The office id as the URL carries it; not yet checked against the known offices. */
  officeId: number | null;
  selectedWeek: IDateOption;
  onlyMyTeam: boolean;
  expandedUserIds: number[];
  setTab: (tab: string | null) => void;
  setOfficeId: (officeId: number | null) => void;
  /** Clearing the week returns to the current week. */
  setWeek: (week: IDateOption | null) => void;
  setOnlyMyTeam: (onlyMyTeam: boolean) => void;
  setExpandedUserIds: (userIds: number[]) => void;
}

/**
 * Time page view state backed by the URL search params, so Back from a job
 * and a shared link both reopen the same view. Changing the tab collapses
 * the expanded people, because the new tab mounts a new table.
 */
export const useTimeViewParams = (): IUseTimeViewParamsResult => {
  const [view, update] = useSearchParamsState(
    readTimeViewParams,
    writeTimeViewParams,
  );

  const setTab = useCallback(
    (tab: string | null) =>
      update((current) =>
        current.tab === tab ? null : { tab, expandedUserIds: [] },
      ),
    [update],
  );

  const setOfficeId = useCallback(
    (officeId: number | null) => update(() => ({ officeId })),
    [update],
  );

  const setWeek = useCallback(
    (week: IDateOption | null) => update(() => ({ weekId: week?.id ?? null })),
    [update],
  );

  const setOnlyMyTeam = useCallback(
    (onlyMyTeam: boolean) => update(() => ({ onlyMyTeam })),
    [update],
  );

  const setExpandedUserIds = useCallback(
    (expandedUserIds: number[]) => update(() => ({ expandedUserIds })),
    [update],
  );

  const selectedWeek = useMemo(
    () => tempOptions.find((o) => o.id === view.weekId) ?? currentWeekFilter,
    [view.weekId],
  );

  return {
    tab: view.tab,
    officeId: view.officeId,
    selectedWeek,
    onlyMyTeam: view.onlyMyTeam,
    expandedUserIds: view.expandedUserIds,
    setTab,
    setOfficeId,
    setWeek,
    setOnlyMyTeam,
    setExpandedUserIds,
  };
};
